/**
 * AtomicRow
 * Top-level row for a standalone (non-saga) atomic workflow in the WorkflowList —
 * e.g. a lease-setup or payment-setup started outside the move-in saga.
 * Shows a type-derived label, progress / outstanding preconditions, and a dismiss button.
 * Clicking the row drills straight into the member detail (level 2) since an atomic
 * has exactly one member and no intermediate chain.
 */

import { realUnitNumber } from '@resida/shared';
import { ChevronRight } from 'lucide-react';
import { DismissWorkflowButton } from './DismissWorkflowButton';
import { useWorkflowPanel } from './useWorkflowPanel';
import { ATOMIC_WORKFLOW_LABELS, type AtomicWorkflowSummary } from '../../types/workflow';

interface AtomicRowProps {
  workflow: AtomicWorkflowSummary;
  onDrillIn: (workflow: AtomicWorkflowSummary) => void;
}

export function AtomicRow({ workflow, onDrillIn }: AtomicRowProps) {
  const { dismiss } = useWorkflowPanel();

  const label = ATOMIC_WORKFLOW_LABELS[workflow.type];
  // Disambiguate same-type standalone rows (e.g. two "Set up payments") for screen readers by
  // qualifying the accessible name with the subject (unit) when one is available.
  const subjectLabel = realUnitNumber(workflow.subjectLabel);
  const qualifiedLabel = subjectLabel ? `${label} · ${subjectLabel}` : label;
  const member = workflow.members[0];
  const unsatisfiedCount = member
    ? member.preconditions.filter((p) => !p.satisfied).length
    : 0;
  const totalSteps = member?.totalSteps ?? 0;
  const currentStep = member?.currentStep ?? null;
  const progressPercent =
    totalSteps > 0 && currentStep != null
      ? Math.round(((currentStep - 1) / totalSteps) * 100)
      : 0;

  return (
    <div
      data-testid={`workflow-atomic-row-${workflow.id}`}
      className="relative flex items-center gap-3 p-3 rounded-lg border border-border bg-background"
      role="listitem"
    >
      {/* Main clickable area */}
      <button
        type="button"
        onClick={() => onDrillIn(workflow)}
        className="flex-1 flex items-center gap-3 text-left min-w-0 hover:bg-accent/30 rounded-md transition-colors"
        aria-label={`Open ${qualifiedLabel} workflow`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{label}</p>
          <p className="text-xs text-muted-foreground">
            {unsatisfiedCount > 0
              ? `${unsatisfiedCount} item${unsatisfiedCount !== 1 ? 's' : ''} needed`
              : `${progressPercent}% done`}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
      </button>

      {/* Dismiss button */}
      <DismissWorkflowButton
        workflowId={workflow.id}
        accessibleLabel={`Dismiss ${qualifiedLabel} workflow`}
        testId={`workflow-dismiss-btn-${workflow.id}`}
        onDismiss={dismiss}
      />
    </div>
  );
}
